import { ButtonRoot, SpinnerContainer, ContentWrapper, SubContentContainer, SubContentIconContainer, MainContentContainer } from './styles.js';
import { ButtonPadding, ButtonSize, ButtonState, ButtonType } from './types.js';
import { ThemingContext, Text, InlineChildren, Icon } from '@doordash/design-language';
import { generateRoleRelatedProps, loadingSizeForButton, loadingStateForButton } from './utils.js';
import Loading from '@doordash/component-loading';
import React from 'react';
import { getPropsToForward } from '@doordash/utilities';
import invariant from 'invariant';

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var validateSubContentString = function validateSubContentString(input) {
  return typeof input === 'string' && input.length > 0 || typeof input === 'number';
}; // Define a default element type to set as the E generic. This will allow TS to use inference
// to properly set `tagProps` to accept any HTML `button` props. If a different `tag` prop
// is provided, TS will be able to infer that and set `tagProps` to the custom `tag` element's props


var defaultElement = 'button';

/**
 * Button component with support for icons, loaders, etc.
 * @see {@link https://prism.doordash.com/components/button | Prism Web Documentation: Button}
 * @see {@link https://s3-design-language-system.cdn4dd.com/master/index.html?path=/story/controls-component-button-button--playground | Prism Web Storybook: Button}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role | Accessibility Guidelines}
 * @public
 * @component
 * @componentAPI {@link ButtonProps}
 * @componentConstant {@link ButtonPadding}
 * @componentConstant {@link ButtonSize}
 * @componentConstant {@link ButtonState}
 * @componentConstant {@link ButtonType}
 * @componentSemantic Button
 */
var Button = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _ref$children = _ref.children,
      children = _ref$children === void 0 ? 'Click Me' : _ref$children,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? ButtonSize.Medium : _ref$size,
      _ref$padding = _ref.padding,
      padding = _ref$padding === void 0 ? ButtonPadding.Default : _ref$padding,
      _ref$isInline = _ref.isInline,
      isInline = _ref$isInline === void 0 ? false : _ref$isInline,
      _ref$isDepressed = _ref.isDepressed,
      isDepressed = _ref$isDepressed === void 0 ? false : _ref$isDepressed,
      _ref$isDisabled = _ref.isDisabled,
      isDisabled = _ref$isDisabled === void 0 ? false : _ref$isDisabled,
      isInRadioGroup = _ref.isInRadioGroup,
      _ref$isToggleable = _ref.isToggleable,
      isToggleable = _ref$isToggleable === void 0 ? false : _ref$isToggleable,
      _ref$isToggled = _ref.isToggled,
      isToggled = _ref$isToggled === void 0 ? false : _ref$isToggled,
      _ref$isRaised = _ref.isRaised,
      isRaised = _ref$isRaised === void 0 ? false : _ref$isRaised,
      leadingIcon = _ref.leadingIcon,
      trailingIcon = _ref.trailingIcon,
      leadingText = _ref.leadingText,
      trailingText = _ref.trailingText,
      _ref$state = _ref.state,
      state = _ref$state === void 0 ? ButtonState.Default : _ref$state,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? ButtonType.Primary : _ref$type,
      id = _ref.id,
      _ref$tag = _ref.tag,
      tag = _ref$tag === void 0 ? defaultElement : _ref$tag,
      onAnimationEnd = _ref.onAnimationEnd,
      onClick = _ref.onClick,
      accessibilityLabel = _ref.accessibilityLabel,
      accessibilityRole = _ref.accessibilityRole,
      accessibilityDescribedBy = _ref.accessibilityDescribedBy,
      href = _ref.href,
      target = _ref.target,
      tagProps = _ref.tagProps,
      props = _objectWithoutProperties(_ref, ["children", "size", "padding", "isInline", "isDepressed", "isDisabled", "isInRadioGroup", "isToggleable", "isToggled", "isRaised", "leadingIcon", "trailingIcon", "leadingText", "trailingText", "state", "type", "id", "tag", "onAnimationEnd", "onClick", "accessibilityLabel", "accessibilityRole", "accessibilityDescribedBy", "href", "target", "tagProps"]);

  var theme = React.useContext(ThemingContext);
  invariant(type !== ButtonType.FlatPrimary && type !== ButtonType.FlatSecondary && type !== ButtonType.Plain ? padding !== ButtonPadding.None : true, "\n      Do not use Button.Padding.None with any type that is not\n      ButtonType.Plain or ButtonType.FlatPrimary or ButtonType.FlatSecondary.\n    ");
  invariant(type === ButtonType.FlatPrimary || type === ButtonType.FlatSecondary ? !isToggleable : true, "\n      Do not set isToggleable to true when type is\n      ButtonType.FlatPrimary or ButtonType.FlatSecondary.\n    ");
  invariant(isRaised ? type === ButtonType.Plain || type === ButtonType.Primary || type === ButtonType.PrimaryPill : true, "\n      You can only use isRaised with Plain, Primary\n      or PrimaryPill type buttons.\n    ");
  invariant(type === ButtonType.Link ? !validateSubContentString(leadingText) && !validateSubContentString(trailingText) : true, "\n      You cannot use leading/trailing text with when type is ButtonType.Link.\n    ");
  invariant(leadingIcon ? !validateSubContentString(leadingText) : true, "\n      You can not combine leadingIcon and leadingText, please only use one leading item at a time.\n    ");
  invariant(trailingIcon ? !validateSubContentString(trailingText) : true, "\n      You can not combine trailingIcon and trailingText, please only use one trailing item at a time.\n    ");
  React.useEffect(function () {
    if (size === ButtonSize.XLarge) {
      // eslint-disable-next-line no-console
      console.warn('Warning! `XLarge` Button Size will be removed in Prism Web v8 - we recommend changing your size to `Large`');
    }
  }, [size]);
  React.useEffect(function () {
    if (type === ButtonType.PrimaryPill || type === ButtonType.SecondaryPill || type === ButtonType.TertiaryPill) {
      // eslint-disable-next-line no-console
      console.warn('Warning! `Pill` type Buttons will be removed in Prism Web v8 - we recommend changing your type and using `Shape` Theming for any shape customizations');
    }
  }, [type]);
  var labelStyles;
  var leadingTextStyles;
  var trailingTextStyles;
  var childSpacing;

  switch (size) {
    case ButtonSize.Small:
      labelStyles = Text.Styles.ButtonSmallTitle;
      leadingTextStyles = Text.Styles.ButtonSmallLeading;
      trailingTextStyles = Text.Styles.ButtonSmallTrailing;
      childSpacing = InlineChildren.Sizes.XxxSmall;
      break;
    // TODO: deprecate XLarge size in v8

    case ButtonSize.Large:
    case ButtonSize.XLarge:
      labelStyles = Text.Styles.ButtonLargeTitle;
      leadingTextStyles = Text.Styles.ButtonLargeLeading;
      trailingTextStyles = Text.Styles.ButtonLargeTrailing;
      childSpacing = InlineChildren.Sizes.XxSmall;
      break;

    case ButtonSize.Medium:
    default:
      labelStyles = Text.Styles.ButtonMediumTitle;
      leadingTextStyles = Text.Styles.ButtonMediumLeading;
      trailingTextStyles = Text.Styles.ButtonMediumTrailing;
      childSpacing = InlineChildren.Sizes.XxxSmall;
      break;
  }

  var hasLeadingOrTrailingContent = leadingIcon || trailingIcon || validateSubContentString(leadingText) || validateSubContentString(trailingText);
  var renderLeadingContainer = isInline ? leadingIcon || validateSubContentString(leadingText) : hasLeadingOrTrailingContent;
  var renderTrailingContainer = isInline ? trailingIcon || validateSubContentString(trailingText) : hasLeadingOrTrailingContent;
  var iconSize = size === ButtonSize.Small ? Icon.Sizes.Small : Icon.Sizes.Medium;
  var leadingTrailingContentIsIconsOnly = Boolean(leadingIcon || trailingIcon) && !validateSubContentString(leadingText) && !validateSubContentString(trailingText);
  var propsToForward = getPropsToForward({
    props: props,
    prefixesToForward: ['data-', 'aria-']
  });
  return /*#__PURE__*/React.createElement(ButtonRoot, _extends({
    shape: isToggleable ? theme.Shape.ButtonToggle : theme.Shape.ButtonDefault,
    id: id,
    size: size,
    padding: padding,
    isRaised: isRaised,
    isInline: isInline,
    isDepressed: isDepressed,
    kind: type,
    target: target,
    "aria-label": accessibilityLabel,
    "aria-pressed": isToggleable && !isInRadioGroup ? isToggled : undefined,
    "aria-describedby": accessibilityDescribedBy,
    isToggleable: isToggleable,
    isToggled: isToggled,
    ref: ref
  }, propsToForward, generateRoleRelatedProps({
    isDisabled: isDisabled,
    tag: tag,
    href: href,
    state: state,
    onClick: onClick,
    accessibilityRole: accessibilityRole
  }), tagProps), state !== ButtonState.Default && /*#__PURE__*/React.createElement(SpinnerContainer, null, /*#__PURE__*/React.createElement(Loading, {
    size: Loading.Sizes[loadingSizeForButton(size)],
    color: Loading.Colors.CurrentColor,
    state: loadingStateForButton(state),
    onAnimationEnd: onAnimationEnd
  })), /*#__PURE__*/React.createElement(ContentWrapper, {
    isHidden: state !== ButtonState.Default,
    hasLeadingOrTrailingContent: hasLeadingOrTrailingContent,
    size: size,
    kind: type,
    padding: padding,
    tag: "span"
  }, /*#__PURE__*/React.createElement(InlineChildren, {
    tag: "span",
    justifyContent: hasLeadingOrTrailingContent && !isInline ? InlineChildren.Justification.SpaceBetween : InlineChildren.Justification.Center,
    alignItems: InlineChildren.Alignment.Center,
    size: childSpacing
  }, renderLeadingContainer && /*#__PURE__*/React.createElement(SubContentContainer, {
    side: "leading",
    isInline: isInline,
    isOnlyIcons: leadingTrailingContentIsIconsOnly,
    iconSize: iconSize
  }, leadingIcon && /*#__PURE__*/React.createElement(SubContentIconContainer, {
    iconSize: iconSize
  }, /*#__PURE__*/React.createElement(Icon, {
    type: leadingIcon,
    shouldAdjustSmallSizesWhitespace: true,
    size: iconSize,
    color: Icon.Colors.CurrentColor
  })), leadingText && !leadingIcon && /*#__PURE__*/React.createElement(Text, {
    overflow: Text.Overflows.Truncate,
    styles: leadingTextStyles,
    textAlign: Text.TextAlignments.Left,
    color: Text.Colors.CurrentColor
  }, leadingText)), /*#__PURE__*/React.createElement(MainContentContainer, null, /*#__PURE__*/React.createElement(Text, {
    overflow: Text.Overflows.Truncate,
    styles: labelStyles,
    textAlign: Text.TextAlignments.Center,
    color: Text.Colors.CurrentColor,
    textDecoration: type === ButtonType.Link ? Text.TextDecorations.Underline : undefined
  }, children)), renderTrailingContainer && /*#__PURE__*/React.createElement(SubContentContainer, {
    side: "trailing",
    isInline: isInline,
    isOnlyIcons: leadingTrailingContentIsIconsOnly,
    iconSize: iconSize
  }, trailingIcon && /*#__PURE__*/React.createElement(SubContentIconContainer, {
    iconSize: iconSize
  }, /*#__PURE__*/React.createElement(Icon, {
    type: trailingIcon,
    shouldAdjustSmallSizesWhitespace: true,
    size: iconSize,
    color: Icon.Colors.CurrentColor
  })), trailingText && !trailingIcon && /*#__PURE__*/React.createElement(Text, {
    overflow: Text.Overflows.Truncate,
    styles: trailingTextStyles,
    textAlign: Text.TextAlignments.Right,
    color: Text.Colors.CurrentColor
  }, trailingText)))));
}); // Quick and dirty way to add statics here
// https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757#issuecomment-894053907

var ButtonNamespace = Object.assign(Button, {
  Padding: ButtonPadding,
  Sizes: ButtonSize,
  States: ButtonState,
  Types: ButtonType
});

export { ButtonNamespace as default };
